import React, { useState, useCallback, useEffect } from "react";
import useApiEp from "../../hooks/useApiEp";
import {
  Box,
  Grid,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Divider,
  LinearProgress,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ContactsIcon from "@mui/icons-material/Contacts";
import * as XLSX from "xlsx";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { API_ENDPOINT } from "../../utils/constants";
import { getToken } from "../../utils/util";
import VoiceCallLogsTable, { T } from "./VoiceCallLogsTable";

export const VoiceCardStyle = {
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

export const VoiceTooltipStyle = {
  contentStyle: {
    background: "#fff",
    border: `1px solid ${T.border}`,
    color: T.text,
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    fontSize: 12,
  },
  labelStyle: { color: T.muted, fontWeight: 600 },
};

// ─── Helpers ─────────────────────────────────────────────────────
export const fmtDuration = (s) => {
  const n = parseInt(s, 10);
  if (!n || isNaN(n)) return "—";
  return n < 60 ? `${n}s` : `${Math.floor(n / 60)}m ${n % 60}s`;
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const fmtDur = (s) => {
  const n = parseInt(s, 10);
  if (!n || isNaN(n)) return "";
  return n < 60 ? `${n}s` : `${Math.floor(n / 60)}m ${n % 60}s`;
};

// ─── Stat Card ───────────────────────────────────────────────────
export const VoiceReportStatCard = ({
  label,
  value,
  icon,
  color,
  bg,
  border,
  loading,
}) => (
  <Card sx={{ ...VoiceCardStyle, border: `1px solid ${border}` }}>
    <CardContent sx={{ p: "16px 18px", "&:last-child": { pb: "16px" } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              color: T.muted,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 800,
              color: T.text,
              lineHeight: 1.2,
              mt: 0.5,
            }}
          >
            {loading ? "—" : value ?? "—"}
          </Typography>
        </Box>
        <Box
          sx={{
            background: bg,
            borderRadius: 1.5,
            p: "8px",
            color,
            display: "flex",
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Shared: Campaign Checkbox List ─────────────────────────────
const CampaignCheckList = ({
  campaigns,
  selected,
  onChange, // Replaces onToggle. Parent should do: (newSelected) => setSelected(newSelected)
  accentColor,
  accentBg,
  accentBorder,
}) => {
  const allSelected =
    campaigns.length > 0 && selected.length === campaigns.length;
  const someSelected = selected.length > 0 && !allSelected;

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      onChange(campaigns); // Select all
    } else {
      onChange([]); // Clear all
    }
  };

  return (
    <>
      {/* Top Header Section (Preserved) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAll}
              icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 20 }} />}
              checkedIcon={<CheckBoxIcon sx={{ fontSize: 20 }} />}
              sx={{
                color: T.border,
                "&.Mui-checked": { color: accentColor },
                "&.MuiCheckbox-indeterminate": { color: accentColor },
                p: "6px",
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>
              Select All ({campaigns.length})
            </Typography>
          }
        />
        {selected.length > 0 && (
          <Chip
            label={`${selected.length} selected`}
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              background: accentBg,
              color: accentColor,
              border: `1px solid ${accentBorder}`,
              fontWeight: 600,
            }}
          />
        )}
      </Box>
      <Divider sx={{ mb: 1.5 }} />

      {/* Autocomplete Section */}
      <Autocomplete
        multiple
        options={campaigns}
        value={selected}
        disableCloseOnSelect
        // Update the selected array whenever selection changes
        onChange={(event, newValue) => {
          onChange(newValue);
        }}
        // Render custom styled tags (chips) inside the input
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={option}
              size="small"
              sx={{
                background: accentBg,
                color: accentColor,
                border: `1px solid ${accentBorder}`,
                fontWeight: 500,
                fontSize: 12,
              }}
            />
          ))
        }
        // Retain your custom checkbox look for each dropdown option
        renderOption={(props, option, { selected: isSelected }) => (
          <li {...props} style={{ ...props.style, padding: "4px 8px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                px: 1.5,
                py: 0.8,
                borderRadius: 1.5,
                border: `1px solid ${
                  isSelected ? accentBorder : "transparent"
                }`,
                background: isSelected ? accentBg : "transparent",
                transition: "all 0.15s",
              }}
            >
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                style={{ marginRight: 8 }}
                checked={isSelected}
                sx={{
                  color: T.subtle,
                  "&.Mui-checked": { color: accentColor },
                  p: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 13,
                  color: isSelected ? accentColor : T.textSecondary,
                  fontWeight: isSelected ? 600 : 400,
                }}
              >
                {option}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selected.length === 0 ? "Search campaigns..." : ""}
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                p: 0.5,
                "& fieldset": { borderColor: T.borderLight },
                "&:hover fieldset": { borderColor: T.border },
                "&.Mui-focused fieldset": { borderColor: accentColor },
              },
            }}
          />
        )}
      />
    </>
  );
};

// ─── Multi Campaign Export Dialog ────────────────────────────────
const MultiExportDialog = ({ open, onClose, campaigns, filters }) => {
  const [selected, setSelected] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    if (exporting) return;
    setSelected([]);
    setError("");
    onClose();
  };

  const handleExport = async () => {
    if (selected.length === 0) return;
    setExporting(true);
    setError("");
    try {
      const body = {
        campaigns: selected,
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.answer_status && { answer_status: filters.answer_status }),
      };
      const res = await fetch(`${API_ENDPOINT}/voice/multi-campaigns-export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const json = await res.json();
      const rows = json.data || [];
      if (rows.length === 0) {
        setError("No records found for the selected campaigns.");
        setExporting(false);
        return;
      }

      const wb = XLSX.utils.book_new();
      const byCampaign = rows.reduce((acc, r) => {
        const k = r.campaign_name || "Unknown";
        if (!acc[k]) acc[k] = [];
        acc[k].push(r);
        return acc;
      }, {});

      Object.entries(byCampaign).forEach(([campName, campRows]) => {
        const ws = XLSX.utils.json_to_sheet(
          campRows.map((r, i) => ({
            "#": i + 1,
            Mobile: r.mobile,
            Campaign: r.campaign_name,
            "CLI (Caller ID)": r.cli,
            "Answer Status": r.answer_status,
            "DTMF Key": r.dtmf || "",
            "Call Duration (billing)": fmtDur(r.billing_duration),
            "Patch Duration (s)": r.patch_duration || "",
            "Start Time": r.start_time,
            "Answer Time": r.answer_time,
            "End Time": r.end_time,
            "Hangup Cause": r.hangup_cause,
            "Agent Number": r.agent_number,
            "Agent Answer Time": r.agent_answer_time,
            "Bridge Hangup Cause": r.bridge_hangup_cause,
            "Retry Count": r.retry_count,
            "Recording URL": r.recording_file,
            "Created On": r.createdOn
              ? new Date(r.createdOn).toLocaleString("en-IN")
              : "",
          }))
        );
        ws["!cols"] = [
          4, 14, 22, 16, 14, 10, 20, 18, 20, 20, 20, 22, 16, 20, 20, 12, 50, 22,
        ].map((w) => ({ wch: w }));
        XLSX.utils.book_append_sheet(wb, ws, campName.slice(0, 31));
      });

      const wsSummary = XLSX.utils.aoa_to_sheet([
        ["Multi-Campaign Voice Report"],
        ["Generated On", new Date().toLocaleString("en-IN")],
        ["Total Records", rows.length],
        ["Campaigns Selected", selected.length],
        [],
        [
          "Campaign",
          "Total Calls",
          "Answered",
          "Not Answered",
          "Answer Rate %",
        ],
        ...Object.entries(byCampaign).map(([name, campRows]) => {
          const ans = campRows.filter(
            (r) => r.answer_status === "Answered"
          ).length;
          return [
            name,
            campRows.length,
            ans,
            campRows.length - ans,
            campRows.length > 0
              ? ((ans / campRows.length) * 100).toFixed(1)
              : "0.0",
          ];
        }),
      ]);
      wsSummary["!cols"] = [30, 12, 12, 14, 14].map((w) => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
      XLSX.writeFile(
        wb,
        `multi-campaign-report-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      handleClose();
    } catch (err) {
      console.error("Multi-export failed:", err);
      setError("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          borderBottom: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            background: T.purpleBg,
            border: `1px solid ${T.purpleBorder}`,
            borderRadius: 1.5,
            p: "6px",
            display: "flex",
            color: T.purple,
          }}
        >
          <FileDownloadIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: T.text }}>
            Multi-Campaign Export
          </Typography>
          <Typography sx={{ fontSize: 12, color: T.muted, mt: 0.2 }}>
            Select campaigns to include in the export
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, fontSize: 13 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}
        <CampaignCheckList
          campaigns={campaigns}
          selected={selected}
          onChange={(newSelected) => setSelected(newSelected)} // Just pass the new array directly!
          accentColor={T.purple}
          accentBg={T.purpleBg}
          accentBorder={T.purpleBorder}
        />
        {exporting && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              sx={{
                borderRadius: 2,
                background: T.purpleBg,
                "& .MuiLinearProgress-bar": { background: T.purple },
              }}
            />
            <Typography
              sx={{
                fontSize: 11,
                color: T.muted,
                mt: 0.8,
                textAlign: "center",
              }}
            >
              Fetching records and building Excel…
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: `1px solid ${T.border}`, gap: 1 }}
      >
        <Button
          onClick={handleClose}
          disabled={exporting}
          variant="outlined"
          size="small"
          sx={{
            color: T.muted,
            borderColor: T.border,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 500,
            "&:hover": { borderColor: "#9ca3af", background: T.headerBg },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          disabled={selected.length === 0 || exporting}
          variant="contained"
          size="small"
          startIcon={!exporting && <DownloadIcon sx={{ fontSize: 16 }} />}
          sx={{
            background: T.purple,
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            px: 2.5,
            boxShadow: "none",
            "&:hover": { background: "#6d28d9", boxShadow: "none" },
            "&.Mui-disabled": { background: T.borderLight, color: T.subtle },
          }}
        >
          {exporting
            ? "Exporting…"
            : `Export ${
                selected.length > 0 ? `(${selected.length})` : ""
              } Campaign${selected.length !== 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Contacts Export Dialog ──────────────────────────────────────
const ContactsExportDialog = ({ open, onClose, campaigns, filters }) => {
  const [step, setStep] = useState(1);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setGroupsLoading(true);
    fetch(`${API_ENDPOINT}/user/group-list`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.json())
      .then((json) => setGroups(Array.isArray(json) ? json : []))
      .catch(() => setGroups([]))
      .finally(() => setGroupsLoading(false));
  }, [open]);

  const handleClose = () => {
    if (exporting) return;
    setStep(1);
    setSelectedCampaigns([]);
    setSelectedGroup(null);
    setError("");
    onClose();
  };

  const handleExport = async () => {
    if (!selectedGroup || selectedCampaigns.length === 0) return;
    setExporting(true);
    setError("");
    try {
      const body = {
        campaigns: selectedCampaigns,
        groupName: selectedGroup,
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.answer_status && { answer_status: filters.answer_status }),
        ...(filters.dtmf && { dtmf: filters.dtmf }),
        ...(filters.search && { search: filters.search }),
      };

      const res = await fetch(
        `${API_ENDPOINT}/voice/report-export-with-contact-details`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const json = await res.json();
      const rows = json.data || [];
      if (rows.length === 0) {
        setError("No records found for the selected campaigns and group.");
        setExporting(false);
        return;
      }

      // Detect dynamic contact keys
      const reportKeys = new Set([
        "_id",
        "campaign_name",
        "mobile",
        "cli",
        "answer_status",
        "dtmf",
        "billing_duration",
        "start_time",
        "end_time",
        "answer_time",
        "hangup_cause",
        "agent_number",
        "agent_answer_time",
        "bridge_hangup_cause",
        "patch_duration",
        "retry_count",
        "recording_file",
        "createdOn",
        "customerId",
      ]);
      const contactKeys = Object.keys(rows[0] || {}).filter(
        (k) => !reportKeys.has(k)
      );

      const wb = XLSX.utils.book_new();
      const byCampaign = rows.reduce((acc, r) => {
        const k = r.campaign_name || "Unknown";
        if (!acc[k]) acc[k] = [];
        acc[k].push(r);
        return acc;
      }, {});

      Object.entries(byCampaign).forEach(([campName, campRows]) => {
        const ws = XLSX.utils.json_to_sheet(
          campRows.map((r, i) => {
            const base = {
              "#": i + 1,
              Mobile: r.mobile,
              Campaign: r.campaign_name,
              "CLI (Caller ID)": r.cli,
              "Answer Status": r.answer_status,
              "DTMF Key": r.dtmf || "",
              "Call Duration (billing)": fmtDur(r.billing_duration),
              "Patch Duration (s)": r.patch_duration || "",
              "Start Time": r.start_time,
              "Answer Time": r.answer_time,
              "End Time": r.end_time,
              "Hangup Cause": r.hangup_cause,
              "Agent Number": r.agent_number,
              "Agent Answer Time": r.agent_answer_time,
              "Bridge Hangup Cause": r.bridge_hangup_cause,
              "Retry Count": r.retry_count,
              "Recording URL": r.recording_file,
              "Created On": r.createdOn
                ? new Date(r.createdOn).toLocaleString("en-IN")
                : "",
            };
            contactKeys.forEach((k) => {
              base[
                k
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (s) => s.toUpperCase())
              ] = r[k] ?? "";
            });
            return base;
          })
        );
        ws["!cols"] = [
          ...[
            4, 14, 22, 16, 14, 10, 20, 18, 20, 20, 20, 22, 16, 20, 20, 12, 50,
            22,
          ].map((w) => ({ wch: w })),
          ...contactKeys.map(() => ({ wch: 20 })),
        ];
        XLSX.utils.book_append_sheet(wb, ws, campName.slice(0, 31));
      });

      const wsSummary = XLSX.utils.aoa_to_sheet([
        ["Voice + Contact Report"],
        ["Generated On", new Date().toLocaleString("en-IN")],
        ["Contact Group", selectedGroup],
        ["Total Records", rows.length],
        [],
        [
          "Campaign",
          "Total Calls",
          "Answered",
          "Not Answered",
          "Answer Rate %",
        ],
        ...Object.entries(byCampaign).map(([name, campRows]) => {
          const ans = campRows.filter(
            (r) => r.answer_status === "Answered"
          ).length;
          return [
            name,
            campRows.length,
            ans,
            campRows.length - ans,
            campRows.length > 0
              ? ((ans / campRows.length) * 100).toFixed(1)
              : "0.0",
          ];
        }),
      ]);
      wsSummary["!cols"] = [30, 12, 12, 14, 14].map((w) => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
      XLSX.writeFile(
        wb,
        `contacts-report-${selectedGroup.replace(/\s+/g, "_")}-${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`
      );
      handleClose();
    } catch (err) {
      console.error("Contacts export failed:", err);
      setError("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const StepDot = ({ n, active, done }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          background: done ? T.teal : active ? T.tealBg : T.headerBg,
          color: done ? "#fff" : active ? T.teal : T.subtle,
          border: `1.5px solid ${
            done ? T.teal : active ? T.tealBorder : T.border
          }`,
          transition: "all 0.2s",
        }}
      >
        {done ? "✓" : n}
      </Box>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: active || done ? 600 : 400,
          color: active || done ? T.teal : T.subtle,
        }}
      >
        {n === 1 ? "Campaigns" : "Contact Group"}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          borderBottom: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            background: T.tealBg,
            border: `1px solid ${T.tealBorder}`,
            borderRadius: 1.5,
            p: "6px",
            display: "flex",
            color: T.teal,
          }}
        >
          <ContactsIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: T.text }}>
            Contacts Export
          </Typography>
          <Typography sx={{ fontSize: 12, color: T.muted, mt: 0.2 }}>
            Export call report merged with contact group data
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <StepDot n={1} active={step === 1} done={step > 1} />
          <Box
            sx={{
              width: 20,
              height: 1,
              background: step > 1 ? T.teal : T.border,
            }}
          />
          <StepDot n={2} active={step === 2} done={false} />
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2, minHeight: 300 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, fontSize: 13 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {/* Step 1 — Campaign selection */}
        {step === 1 && (
          <CampaignCheckList
            campaigns={campaigns}
            selected={selectedCampaigns}
            onChange={(newSelected) => setSelectedCampaigns(newSelected)} // Just pass the new array directly!
            accentColor={T.purple}
            accentBg={T.purpleBg}
            accentBorder={T.purpleBorder}
          />
        )}

        {/* Step 2 — Group selection */}
        {step === 2 && (
          <>
            {/* Selected campaigns summary */}
            <Box
              sx={{
                mb: 2.5,
                p: 1.5,
                borderRadius: 1.5,
                background: T.tealBg,
                border: `1px solid ${T.tealBorder}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  color: T.teal,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  mb: 0.4,
                }}
              >
                Selected Campaigns ({selectedCampaigns.length})
              </Typography>
              <Typography sx={{ fontSize: 12, color: T.teal, lineHeight: 1.6 }}>
                {selectedCampaigns.join(", ")}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: 13,
                color: T.textSecondary,
                fontWeight: 600,
                mb: 1.2,
              }}
            >
              Choose a contact group to merge with the report
            </Typography>

            <Autocomplete
              options={groups}
              value={selectedGroup}
              onChange={(_, val) => setSelectedGroup(val)}
              loading={groupsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contact Group"
                  placeholder="Search or select a group…"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: "#fff",
                      "& fieldset": { borderColor: T.border },
                      "&:hover fieldset": { borderColor: "#9ca3af" },
                      "&.Mui-focused fieldset": { borderColor: T.teal },
                    },
                    "& .MuiInputLabel-root": { color: T.muted },
                    "& .MuiInputLabel-root.Mui-focused": { color: T.teal },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {groupsLoading ? (
                          <CircularProgress size={16} sx={{ color: T.teal }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={
                groupsLoading ? "Loading groups…" : "No groups found"
              }
              sx={{ mb: 1 }}
            />

            {exporting && (
              <Box sx={{ mt: 2.5 }}>
                <LinearProgress
                  sx={{
                    borderRadius: 2,
                    background: T.tealBg,
                    "& .MuiLinearProgress-bar": { background: T.teal },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 11,
                    color: T.muted,
                    mt: 0.8,
                    textAlign: "center",
                  }}
                >
                  Merging contact data and building Excel…
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: `1px solid ${T.border}`, gap: 1 }}
      >
        <Button
          onClick={handleClose}
          disabled={exporting}
          variant="outlined"
          size="small"
          sx={{
            color: T.muted,
            borderColor: T.border,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 500,
            "&:hover": { borderColor: "#9ca3af", background: T.headerBg },
          }}
        >
          Cancel
        </Button>

        {step === 1 ? (
          <Button
            onClick={() => setStep(2)}
            disabled={selectedCampaigns.length === 0}
            variant="contained"
            size="small"
            sx={{
              background: T.teal,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              px: 2.5,
              boxShadow: "none",
              "&:hover": { background: "#0f766e", boxShadow: "none" },
              "&.Mui-disabled": { background: T.borderLight, color: T.subtle },
            }}
          >
            Next: Choose Group →
          </Button>
        ) : (
          <>
            <Button
              onClick={() => {
                setStep(1);
                setError("");
              }}
              disabled={exporting}
              variant="outlined"
              size="small"
              sx={{
                color: T.teal,
                borderColor: T.tealBorder,
                textTransform: "none",
                fontSize: 13,
                fontWeight: 500,
                "&:hover": { borderColor: T.teal, background: T.tealBg },
              }}
            >
              ← Back
            </Button>
            <Button
              onClick={handleExport}
              disabled={!selectedGroup || exporting}
              variant="contained"
              size="small"
              startIcon={!exporting && <DownloadIcon sx={{ fontSize: 16 }} />}
              sx={{
                background: T.teal,
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                px: 2.5,
                boxShadow: "none",
                "&:hover": { background: "#0f766e", boxShadow: "none" },
                "&.Mui-disabled": {
                  background: T.borderLight,
                  color: T.subtle,
                },
              }}
            >
              {exporting
                ? "Exporting…"
                : `Export with "${selectedGroup || "…"}"`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ──────────────────────────────────────────────
const VoiceFullReports = () => {
  const [filters, setFilters] = useState({
    answer_status: "",
    campaign_name: "",
    dtmf: "",
    startDate: "",
    endDate: "",
    search: "",
  });
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [multiExportOpen, setMultiExportOpen] = useState(false);
  const [contactsExportOpen, setContactsExportOpen] = useState(false);

  const buildQS = useCallback(
    (extra = {}) => {
      const p = new URLSearchParams();
      Object.entries({ ...filters, ...extra }).forEach(
        ([k, v]) => v && p.set(k, v)
      );
      return p.toString();
    },
    [filters]
  );

  const { data: stats = {}, isLoading: sLoad } = useApiEp(
    `/voice/get-voice-report-stats?${buildQS()}`
  );
  const { data: listRes = {}, isLoading: lLoad } = useApiEp(
    `/voice/get-voice-report-hooks?${buildQS({ page, limit: 15 })}`
  );

  const setF = (k, v) => {
    setFilters((p) => ({ ...p, [k]: v }));
    setPage(1);
  };
  const clearFilters = () => {
    setFilters({
      answer_status: "",
      campaign_name: "",
      dtmf: "",
      startDate: "",
      endDate: "",
      search: "",
    });
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const list = listRes.data || [];
  const campaigns = stats.byCampaign || [];

  const pieData = [
    { name: "Answered", value: stats.answeredCalls || 0, color: T.green },
    { name: "Not Answered", value: stats.notAnsweredCalls || 0, color: T.red },
  ];

  const statCards = [
    {
      label: "Total Calls",
      value: stats.totalCalls,
      icon: <PhoneIcon sx={{ fontSize: 20 }} />,
      color: T.blue,
      bg: T.blueBg,
      border: T.blueBorder,
    },
    {
      label: "Answered",
      value: stats.answeredCalls,
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />,
      color: T.green,
      bg: T.greenBg,
      border: T.greenBorder,
    },
    {
      label: "Not Answered",
      value: stats.notAnsweredCalls,
      icon: <HighlightOffIcon sx={{ fontSize: 20 }} />,
      color: T.red,
      bg: T.redBg,
      border: T.redBorder,
    },
    {
      label: "Answer Rate",
      value: stats.answerRate != null ? `${stats.answerRate}%` : "—",
      icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
      color: T.purple,
      bg: T.purpleBg,
      border: T.purpleBorder,
    },
    {
      label: "Avg Duration",
      value: fmtDuration(stats.avgDuration),
      icon: <TimerOutlinedIcon sx={{ fontSize: 20 }} />,
      color: T.amber,
      bg: T.amberBg,
      border: T.amberBorder,
    },
  ];

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      background: "#fff",
      "& fieldset": { borderColor: T.border },
      "&:hover fieldset": { borderColor: "#9ca3af" },
      "&.Mui-focused fieldset": { borderColor: T.blue },
    },
    "& .MuiInputLabel-root": { color: T.muted },
    "& .MuiInputLabel-root.Mui-focused": { color: T.blue },
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(
        `${API_ENDPOINT}/voice/get-voice-report-export?${buildQS()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const json = await res.json();
      const rows = json.data || [];

      const ws = XLSX.utils.json_to_sheet(
        rows.map((r, i) => ({
          "#": i + 1,
          Mobile: r.mobile,
          Campaign: r.campaign_name,
          "CLI (Caller ID)": r.cli,
          "Answer Status": r.answer_status,
          "DTMF Key": r.dtmf || "",
          "Call Duration (billing)": fmtDur(r.billing_duration),
          "Patch Duration (s)": r.patch_duration || "",
          "Start Time": r.start_time,
          "Answer Time": r.answer_time,
          "End Time": r.end_time,
          "Hangup Cause": r.hangup_cause,
          "Agent Number": r.agent_number,
          "Agent Answer Time": r.agent_answer_time,
          "Bridge Hangup Cause": r.bridge_hangup_cause,
          "Retry Count": r.retry_count,
          "Recording URL": r.recording_file,
          "Created On": r.createdOn
            ? new Date(r.createdOn).toLocaleString("en-IN")
            : "",
        }))
      );
      ws["!cols"] = [
        4, 14, 22, 16, 14, 10, 20, 18, 20, 20, 20, 22, 16, 20, 20, 12, 50, 22,
      ].map((w) => ({ wch: w }));

      const wsSummary = XLSX.utils.aoa_to_sheet([
        ["Voice Campaign Report"],
        ["Generated On", new Date().toLocaleString("en-IN")],
        ["Total Records", rows.length],
        [],
        ["Filter Applied", "Value"],
        ["Answer Status", filters.answer_status || "All"],
        ["Campaign", filters.campaign_name || "All"],
        ["DTMF Key", filters.dtmf || "All"],
        ["Mobile Search", filters.search || "—"],
        ["From Date", filters.startDate || "—"],
        ["To Date", filters.endDate || "—"],
        [],
        ["Answer Status", "Count"],
        ...Object.entries(
          rows.reduce((acc, r) => {
            acc[r.answer_status] = (acc[r.answer_status] || 0) + 1;
            return acc;
          }, {})
        ).map(([k, v]) => [k, v]),
      ]);
      wsSummary["!cols"] = [{ wch: 22 }, { wch: 28 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Voice Report");
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
      XLSX.writeFile(
        wb,
        `voice-report-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 1.5, md: 3 },
        background: T.bg,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: T.text, letterSpacing: "-0.5px" }}
          >
            Voice Campaign Reports
          </Typography>
          <Typography variant="body2" sx={{ color: T.muted, mt: 0.4 }}>
            {sLoad
              ? "Loading…"
              : `${stats.totalCalls ?? 0} total calls · ${
                  campaigns.length
                } campaigns`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContactsIcon sx={{ fontSize: 17 }} />}
            onClick={() => setContactsExportOpen(true)}
            disabled={campaigns.length === 0}
            sx={{
              color: T.teal,
              borderColor: T.tealBorder,
              background: T.tealBg,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              px: 2,
              boxShadow: "none",
              "&:hover": {
                background: "#ccfbf1",
                borderColor: T.teal,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                background: T.borderLight,
                color: T.subtle,
                borderColor: T.border,
              },
            }}
          >
            Contacts Export
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon sx={{ fontSize: 17 }} />}
            onClick={() => setMultiExportOpen(true)}
            disabled={campaigns.length === 0}
            sx={{
              color: T.purple,
              borderColor: T.purpleBorder,
              background: T.purpleBg,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              px: 2,
              boxShadow: "none",
              "&:hover": {
                background: "#ede9fe",
                borderColor: T.purple,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                background: T.borderLight,
                color: T.subtle,
                borderColor: T.border,
              },
            }}
          >
            Multi Export
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={
              exporting ? null : <DownloadIcon sx={{ fontSize: 17 }} />
            }
            onClick={handleExport}
            disabled={exporting}
            sx={{
              background: T.green,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              px: 2,
              boxShadow: "none",
              "&:hover": { background: "#15803d", boxShadow: "none" },
              "&.Mui-disabled": { background: T.borderLight, color: T.subtle },
            }}
          >
            {exporting
              ? "Exporting…"
              : `Export Excel${
                  listRes.totalItems ? ` (${listRes.totalItems})` : ""
                }`}
          </Button>
        </Stack>
      </Box>

      <ContactsExportDialog
        open={contactsExportOpen}
        onClose={() => setContactsExportOpen(false)}
        campaigns={campaigns}
        filters={filters}
      />
      <MultiExportDialog
        open={multiExportOpen}
        onClose={() => setMultiExportOpen(false)}
        campaigns={campaigns}
        filters={filters}
      />

      {/* Filters */}
      <Card sx={{ ...VoiceCardStyle, mb: 3 }}>
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${T.borderLight}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <FilterListIcon sx={{ fontSize: 16, color: T.muted }} />
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}
          >
            Filters
          </Typography>
          {activeFilterCount > 0 && (
            <Chip
              label={`${activeFilterCount} active`}
              size="small"
              sx={{
                height: 18,
                fontSize: 11,
                background: T.blueBg,
                color: T.blue,
                border: `1px solid ${T.blueBorder}`,
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search mobile number…"
                value={filters.search}
                onChange={(e) => setF("search", e.target.value)}
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: T.subtle }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {[
              {
                label: "Answer Status",
                key: "answer_status",
                opts: ["Answered", "Not Answered", "Busy", "Failed"],
              },
              { label: "Campaign", key: "campaign_name", opts: campaigns },
              {
                label: "DTMF Key",
                key: "dtmf",
                opts: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
              },
            ].map(({ label, key, opts }) => (
              <Grid item xs={6} sm={4} md={2} key={key}>
                <Autocomplete
                  size="small"
                  options={opts}
                  // Autocomplete expects `null` for an empty state when using strings
                  value={filters[key] || null}
                  onChange={(event, newValue) => {
                    // When the user clicks the "Clear (X)" button, newValue becomes null.
                    // We fall back to "" to match your existing state logic for "All".
                    setF(key, newValue || "");
                  }}
                  // Custom renderOption to handle very long text gracefully
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props;
                    return (
                      <li
                        key={key}
                        {...restProps}
                        style={{ wordBreak: "break-word" }}
                      >
                        {option}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={label} sx={inputSx} />
                  )}
                />
              </Grid>
            ))}
            <Grid item xs={6} sm={4} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={filters.startDate}
                onChange={(e) => setF("startDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={filters.endDate}
                onChange={(e) => setF("endDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={inputSx}
              />
            </Grid>
            {activeFilterCount > 0 && (
              <Grid item xs={12} md="auto">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{
                    color: T.muted,
                    borderColor: T.border,
                    textTransform: "none",
                    fontSize: 13,
                    "&:hover": {
                      borderColor: "#9ca3af",
                      background: T.headerBg,
                    },
                  }}
                >
                  Clear all
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((s) => (
          <Grid item xs={6} sm={4} md={12 / 5} key={s.label}>
            <VoiceReportStatCard {...s} loading={sLoad} />
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ ...VoiceCardStyle, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 0.5,
                }}
              >
                Answer Rate
              </Typography>
              <Box sx={{ flex: 1, position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="46%"
                      innerRadius={58}
                      outerRadius={82}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {pieData.map((e) => (
                        <Cell key={e.name} fill={e.color} />
                      ))}
                    </Pie>
                    <RTooltip {...VoiceTooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute",
                    top: "40%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: T.text,
                      lineHeight: 1,
                    }}
                  >
                    {stats.answerRate ?? 0}%
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: T.muted,
                      mt: 0.3,
                      fontWeight: 500,
                    }}
                  >
                    answer rate
                  </Typography>
                </Box>
              </Box>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 0.5 }}
              >
                {pieData.map((e) => (
                  <Box
                    key={e.name}
                    sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: e.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 11, color: T.muted, fontWeight: 500 }}
                    >
                      {e.name}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ ...VoiceCardStyle, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 1,
                }}
              >
                Daily Volume
              </Typography>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={stats.byDate || []}
                    margin={{ top: 4, right: 16, left: -24, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.borderLight}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: T.subtle, fontSize: 10 }}
                      tickFormatter={(d) => d?.slice(5)}
                    />
                    <YAxis tick={{ fill: T.subtle, fontSize: 10 }} />
                    <RTooltip {...VoiceTooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11, color: T.muted }} />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke={T.blue}
                      strokeWidth={2.5}
                      dot={false}
                      name="Total"
                    />
                    <Line
                      type="monotone"
                      dataKey="answered"
                      stroke={T.green}
                      strokeWidth={2.5}
                      dot={false}
                      name="Answered"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ ...VoiceCardStyle, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 1,
                }}
              >
                DTMF Keys Pressed
              </Typography>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.byDtmf || []}
                    margin={{ top: 4, right: 8, left: -24, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.borderLight}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="dtmf"
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    />
                    <YAxis tick={{ fill: T.subtle, fontSize: 10 }} />
                    <RTooltip {...VoiceTooltipStyle} />
                    <Bar
                      dataKey="count"
                      fill={T.purple}
                      radius={[4, 4, 0, 0]}
                      name="Calls"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <VoiceCallLogsTable
        rows={list}
        loading={lLoad}
        page={page}
        totalPages={listRes.totalPages}
        totalItems={listRes.totalItems}
        onPageChange={(v) => setPage(v)}
      />
    </Box>
  );
};

export default VoiceFullReports;
